import React, {useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography} from "@mui/material";
import {requestRenamePaymentMethod} from "./request/payment-method-requests";
import Busy from "../Busy";
import {styled} from "@mui/material/styles";
import {Account} from "../../types/Account";
import {PaymentMethod} from "../../types/PaymentMethod";

const DialogRowGap10 = styled(Grid)(() => ({
  rowGap: '0.71rem',
}));

const DialogBackgroundGrey100 = styled(DialogActions)(({theme}) => ({
  backgroundColor: theme.palette.grey[100],
}));

interface RenamePaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (response: unknown) => void;
  account: Pick<Account, 'id'>;
  paymentMethod?: Pick<PaymentMethod, 'id' | 'nickName'> | null;
}

const RenamePaymentMethodDialog = ({
    open,
    onClose,
    onSuccess,
    account,
    paymentMethod,
  }: RenamePaymentMethodDialogProps) => {
  const [nickName, setNickName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open && paymentMethod?.nickName) {
      setNickName(paymentMethod.nickName);
    }
  }, [open, paymentMethod]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.target.value.length > 30) return;
    setNickName(e.target.value);
  };

  const resetDialog = (): void => {
    setNickName('');
    setErrorMsg('');
  };

  const handleClose = (): void => {
    resetDialog();
    onClose();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    // The dialog is only opened with a payment method selected; guard to satisfy strict null checks
    if (!paymentMethod) return;
    const data = {
      authorityId: account.id, // Request method maps authorityId to accountId
      paymentMethodId: paymentMethod.id,
      nickName: nickName,
    };
    const handleSuccess = (response: unknown): void => {
      Busy.set(false);
      resetDialog();
      onSuccess(response);
    };
    const handleError = (): void => {
      Busy.set(false);
      setErrorMsg('Failed to update nickname. If this error persists, please contact support.');
    };
    Busy.set(true);
    requestRenamePaymentMethod(data, handleSuccess, handleError);
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby={'ss-rename-payment-method'}>
      <DialogTitle id={'ss-rename-payment-method'}>
        Rename Payment Method
      </DialogTitle>
      <DialogContent>
        <form id={'ss-rename-payment-method-form'} onSubmit={handleSubmit} noValidate className={'w-100'}>
          <DialogRowGap10 container direction={'column'}>
            <TextField
              onChange={handleChange}
              name={'nickname'}
              label={'Nickname'}
              value={nickName}
              placeholder={'Enter a new nickname'}
              helperText={'Nicknames can be up to 30 characters long'}
              error={!!errorMsg}
            />
            {
              errorMsg &&
              <Typography variant="body1" component="div" color="error" align="center">{errorMsg}</Typography>
            }
          </DialogRowGap10>
        </form>
      </DialogContent>
      <DialogBackgroundGrey100>
        <Button color={"primary"} onClick={handleClose}>Cancel</Button>
        <Button color={"primary"} type={"submit"} form={"ss-rename-payment-method-form"} >Submit</Button>
      </DialogBackgroundGrey100>
    </Dialog>
  );
};

export default RenamePaymentMethodDialog;
